import React, { useState } from "react";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { motion, AnimatePresence } from "framer-motion";
import { db, doc, updateDoc } from "../../../../firebaseConfig";

import { useAtom } from "jotai";
import { themeAtom, listsAtom } from "../../../../-Atoms";

const { DateTime } = require("luxon");

const ListSelector = ({ jobData, idList, setIdList }) => {
  const [theme] = useAtom(themeAtom);
  const [lists] = useAtom(listsAtom);

  const [menuOpen, setMenuOpen] = useState(false);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [open, setOpen] = useState(false);

  // Move job to different list
  const moveList = async (e) => {
    try {
      const newIdList = e.target.value;
      // Check if the job is already in the target list
      const targetList = lists.find((list) => list.id === newIdList);
      if (targetList && targetList.items.includes(jobData.id)) {
        // Job is already in the target list, no need to move it
        return;
      }

      // Create a mutable copy of jobData to update labels
      const updatedJobData = { ...jobData };

      // Label logic based on target list ID
      if (newIdList === "0367cb77-644f-4d57-b4e8-4286148218d7") {
        // On Hold List
        if (!updatedJobData.idLabels.includes("6285ea079131e28982c19d39")) {
          // On Hold Label
          updatedJobData.idLabels.push("6285ea079131e28982c19d39");
        }
      } else if (newIdList === "bbb73c17-6690-4d43-8569-be12b0d5ee27") {
        // To Be Invoiced List
        if (!updatedJobData.idLabels.includes("5e6881d6af988c41f2bf6e14")) {
          // Invoiced label
          updatedJobData.idLabels.push("5e6881d6af988c41f2bf6e14");
        }
      }

      if (newIdList === "5f081763cd97556d03075c86") {
        console.log("Moving to Shooting List");
        updatedJobData.isInShooting = true;
        updatedJobData.isInShootingTime = DateTime.now()
          .setZone("Australia/Sydney")
          .startOf("day")
          .toUTC()
          .toISO();
      } else {
        console.log("Moving to NOT Shooting List");
        updatedJobData.isInShooting = false;
        updatedJobData.isInShootingTime = null;
      }

      // Remove job from current lists and add to target list
      const updatedLists = lists.map((list) => {
        const newItems = list.items.filter((itemId) => itemId !== jobData.id);
        if (list.id === newIdList) {
          if (!newItems.includes(jobData.id)) {
            newItems.push(jobData.id);
          }
        }
        return { ...list, items: [...new Set(newItems)] }; // Ensure no duplicates
      });

      // Update Firebase for both lists and jobData
      await updateDoc(doc(db, "AdminCollection", "lists"), {
        lists: updatedLists,
      });
      await updateDoc(doc(db, "JobData", jobData.id), {
        idLabels: updatedJobData.idLabels,
        isInShooting: updatedJobData.isInShooting,
        isInShootingTime: updatedJobData.isInShootingTime,
      });

      setIdList(newIdList);
    } catch (error) {
      console.error("Error moving job to list:", error);
    }
  };

  const handleListChange = (event) => {
    moveList(event);
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.05,
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1.0],
      },
    }),
    hover: { scale: 1.03 },
    tap: { scale: 0.98 },
  };

  // Find current list name
  const currentList = lists.find((list) => list.id === idList);
  const selectValue = currentList ? idList : "";

  return (
    <FormControl
      fullWidth
      sx={{
        marginTop: 1,
        position: "relative",
        "& .MuiInputLabel-root": {
          color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
          fontWeight: 500,
          fontSize: "0.95rem",
          transition: "all 0.2s ease",
          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, 7px) scale(0.75)",
          },
        },
        "& .MuiInputLabel-filled": {
          transform: "translate(14px, 16px) scale(1)",
        },
        "& .MuiFilledInput-root": {
          borderRadius: "10px",
          overflow: "hidden",
          backgroundColor:
            theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(240,240,240,0.9)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow:
            theme === "dark"
              ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
              : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
          "&:hover": {
            backgroundColor:
              theme === "dark" ? "rgba(55,55,55,0.8)" : "rgba(255,255,255,1)",
            boxShadow:
              theme === "dark"
                ? `0 4px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.3)`
                : `0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.3)`,
          },
          "&.Mui-focused": {
            boxShadow:
              theme === "dark"
                ? `0 4px 15px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.5)`
                : `0 4px 15px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.5)`,
          },
        },
        "& .MuiSelect-select": {
          display: "flex",
          alignItems: "center",
          padding: "24px 14px 8px 14px",
        },
      }}
      variant="filled"
    >
      <InputLabel id="list-selector">List</InputLabel>
      <Select
        disableUnderline
        labelId="list-selector-select"
        id="list-selector-select"
        value={selectValue}
        label="List"
        onChange={handleListChange}
        open={open}
        onOpen={() => {
          setOpen(true);
          setMenuOpen(true);
        }}
        onClose={() => {
          setOpen(false);
          setMenuOpen(false);
          setHoverIndex(null);
        }}
        renderValue={(selected) => {
          if (!selected) return "";
          const selectedList = lists.find((list) => list.id === selected);
          if (!selectedList) return "";

          return (
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                color: theme === "dark" ? "#ffffff" : "#000000",
              }}
            >
              {selectedList.name}
            </Typography>
          );
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              backgroundColor:
                theme === "dark" ? "rgba(0,0,0,1)" : "rgba(255,255,255,1)",
              "& .MuiMenuItem-root": {
                padding: "10px 16px",
                margin: "1px 3px",
                borderRadius: "8px",
              },
              "& .MuiList-root": {
                padding: "2px",
              },
            },
          },
          TransitionProps: {
            timeout: 100,
          },
        }}
      >
        <AnimatePresence>
          {lists &&
            lists.map((list, index) => {
              const isSelected = idList === list.id;
              const isHovered = hoverIndex === index;

              return (
                <MenuItem
                  key={list.id}
                  value={list.id}
                  onMouseEnter={() => setHoverIndex(index)}
                  onMouseLeave={() => setHoverIndex(null)}
                  onClick={(e) => {
                    e.stopPropagation();

                    handleListChange({
                      target: { value: list.id },
                    });

                    setOpen(false);
                    setMenuOpen(false);
                  }}
                  sx={{
                    backgroundColor: isSelected
                      ? theme === "dark"
                        ? "rgba(100,100,255,0.2)"
                        : "rgba(100,100,255,0.15)"
                      : "transparent",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      backgroundColor: isSelected
                        ? theme === "dark"
                          ? "rgba(100,100,255,0.3)"
                          : "rgba(100,100,255,0.25)"
                        : theme === "dark"
                        ? "rgba(60,60,60,0.5)"
                        : "rgba(240,240,240,0.7)",
                    },
                  }}
                >
                  <motion.div
                    custom={index}
                    initial={menuOpen ? "hidden" : "visible"}
                    animate="visible"
                    variants={itemVariants}
                    whileHover="hover"
                    whileTap="tap"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      padding: "2px 0",
                    }}
                  >
                    <Box
                      component={motion.span}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        fontWeight: isSelected ? 600 : isHovered ? 500 : 400,
                        letterSpacing: isSelected ? "0.01em" : "normal",
                        color: isSelected
                          ? theme === "dark"
                            ? "#ffffff"
                            : "#000000"
                          : theme === "dark"
                          ? "rgba(255,255,255,0.85)"
                          : "rgba(0,0,0,0.85)",
                      }}
                    >
                      {list.name}
                    </Box>
                  </motion.div>
                </MenuItem>
              );
            })}
        </AnimatePresence>
      </Select>
    </FormControl>
  );
};

export default ListSelector;
